import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserPlus, MoreHorizontal, Shield, Key, CheckCircle, XCircle, Clock, UserCheck, Lock, Pencil, Trash2, Ban, PowerOff, Power } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const ROLES = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'direction', label: 'Direction générale' },
  { value: 'teleprospectrice', label: 'Téléprospectrice' },
  { value: 'commercial', label: 'Commercial(e)' },
  { value: 'assistante', label: 'Assistante' },
];

const ROLE_LABELS = {
  super_admin: 'Super Admin', admin: 'Admin', direction: 'Direction',
  teleprospectrice: 'Téléprospectrice', assistante: 'Assistante',
  commercial: 'Commercial(e)', manager: 'Manager'
};

const ROLE_COLORS = {
  super_admin: 'bg-[#FEF2F2] text-[#DC2626]', admin: 'bg-[#EFF6FF] text-[#2563EB]',
  direction: 'bg-[#F0FDF4] text-[#15803D]', teleprospectrice: 'bg-[#FFFBEB] text-[#D97706]',
  assistante: 'bg-[#F3F4F6] text-[#6B7280]', commercial: 'bg-[#F3F4F6] text-[#6B7280]',
  manager: 'bg-[#F0FDF4] text-[#15803D]'
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ prenom: '', nom: '', email: '', password: '', role: 'teleprospectrice' });
  const [resetPw, setResetPw] = useState({ userId: null, password: '' });
  const [permsUser, setPermsUser] = useState(null);
  const [permsSchema, setPermsSchema] = useState(null);
  const [permsOverride, setPermsOverride] = useState(null);
  const [permsEffective, setPermsEffective] = useState(null);
  const [permsUseOverride, setPermsUseOverride] = useState(false);
  const [editUser, setEditUser] = useState(null); // { id, prenom, nom, email, role }

  const loadUsers = async () => {
    try {
      const { data } = await api.get('/users');
      setUsers(data.filter(u => !u.en_attente_validation));
    } catch {}
  };

  const loadPending = async () => {
    try {
      const { data } = await api.get('/auth/pending-users');
      setPendingUsers(data);
    } catch {}
  };

  useEffect(() => { loadUsers(); loadPending(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', form);
      toast.success('Utilisateur créé');
      setShowCreate(false);
      setForm({ prenom: '', nom: '', email: '', password: '', role: 'teleprospectrice' });
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const approveUser = async (userId) => {
    try {
      const { data } = await api.post(`/auth/approve-user/${userId}`);
      toast.success(data.message);
      loadPending(); loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const rejectUser = async (userId) => {
    if (!window.confirm('Rejeter cette demande ?')) return;
    try {
      await api.post(`/auth/reject-user/${userId}`);
      toast.success('Demande rejetée');
      loadPending();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const toggleActive = async (userId, actif) => {
    try {
      await api.put(`/users/${userId}`, { actif: !actif });
      toast.success(actif ? 'Désactivé' : 'Activé');
      loadUsers();
    } catch { toast.error('Erreur'); }
  };

  const changeRole = async (userId, role) => {
    try {
      await api.put(`/users/${userId}`, { role });
      toast.success('Rôle modifié');
      loadUsers();
    } catch { toast.error('Erreur'); }
  };

  const resetPassword = async () => {
    if (!resetPw.password || !resetPw.userId) return;
    try {
      await api.post(`/users/${resetPw.userId}/reset-password`, { password: resetPw.password });
      toast.success('Mot de passe réinitialisé');
      setResetPw({ userId: null, password: '' });
    } catch { toast.error('Erreur'); }
  };

  const openPerms = async (u) => {
    setPermsUser(u);
    setPermsSchema(null);
    setPermsOverride(null);
    setPermsEffective(null);
    setPermsUseOverride(false);
    try {
      const [{ data: schema }, { data: p }] = await Promise.all([
        api.get('/permissions/schema'),
        api.get(`/users/${u.id}/permissions`),
      ]);
      setPermsSchema(schema);
      setPermsEffective(p.effective || {});
      const hasOverride = !!p.override;
      setPermsUseOverride(hasOverride);
      // Initial matrix: existing override OR effective (read-only preview)
      setPermsOverride(hasOverride ? p.override : (p.effective || {}));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement permissions');
      setPermsUser(null);
    }
  };

  const togglePerm = (resource, action) => {
    if (!permsUseOverride) return;
    setPermsOverride(prev => {
      const next = { ...(prev || {}) };
      const list = new Set(next[resource] || []);
      if (list.has(action)) list.delete(action); else list.add(action);
      next[resource] = Array.from(list);
      return next;
    });
  };

  const savePerms = async () => {
    if (!permsUser) return;
    try {
      const payload = permsUseOverride ? permsOverride : {};
      const { data } = await api.put(`/users/${permsUser.id}/permissions`, { permissions: payload });
      setPermsEffective(data.effective || {});
      toast.success('Permissions mises à jour');
      setPermsUser(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement');
    }
  };

  const saveEditUser = async () => {
    if (!editUser) return;
    try {
      await api.put(`/users/${editUser.id}`, {
        prenom: editUser.prenom,
        nom: editUser.nom,
        email: editUser.email,
        role: editUser.role,
      });
      toast.success('Profil mis à jour');
      setEditUser(null);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const hardDeleteUser = async (u) => {
    const confirm1 = window.confirm(
      `⚠️ SUPPRESSION DÉFINITIVE\n\nSupprimer ${u.prenom} ${u.nom} (${u.email}) ?\n\nToutes ses données (templates email, préférences) seront perdues. Cette action est IRRÉVERSIBLE.`
    );
    if (!confirm1) return;
    const typed = window.prompt(`Pour confirmer, tapez le nom "${u.nom}" :`);
    if (typed !== u.nom) {
      toast.error('Nom incorrect, suppression annulée.');
      return;
    }
    try {
      await api.delete(`/users/${u.id}/hard`);
      toast.success('Utilisateur supprimé définitivement');
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Utilisateurs</h1>
          <p className="text-sm text-[#71717A] mt-1">Gestion des comptes, rôles et validations</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button data-testid="create-user-btn" className="h-9 gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white">
              <UserPlus className="w-4 h-4" /> Nouvel utilisateur
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="create-user-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>Créer un utilisateur</DialogTitle>
            </DialogHeader>
            <form onSubmit={createUser} className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm mb-1 block">Prénom</Label>
                  <Input value={form.prenom} onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))} required
                    className="h-9 bg-white" data-testid="user-prenom" />
                </div>
                <div>
                  <Label className="text-sm mb-1 block">Nom</Label>
                  <Input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} required
                    className="h-9 bg-white" data-testid="user-nom" />
                </div>
              </div>
              <div>
                <Label className="text-sm mb-1 block">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required
                  className="h-9 bg-white" data-testid="user-email" />
              </div>
              <div>
                <Label className="text-sm mb-1 block">Mot de passe</Label>
                <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required
                  className="h-9 bg-white" data-testid="user-password" />
              </div>
              <div>
                <Label className="text-sm mb-1 block">Fonction</Label>
                <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                  <SelectTrigger className="h-9 bg-white" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" data-testid="submit-create-user"
                className="w-full h-10 bg-[#F97316] hover:bg-[#EA580C] text-white">Créer</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pending Approvals */}
      {pendingUsers.length > 0 && (
        <Card className="border border-[#F97316]/30 shadow-sm bg-[#FFF7ED]">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold flex items-center gap-2 mb-4">
              <Clock className="w-4 h-4 text-[#F97316]" />
              Demandes en attente de validation
              <Badge className="bg-[#F97316] text-white text-xs">{pendingUsers.length}</Badge>
            </h3>
            <div className="space-y-3">
              {pendingUsers.map(u => (
                <div key={u.id} className="flex items-center gap-4 p-3 bg-white rounded-lg border border-[#E5E7EB]" data-testid={`pending-user-${u.id}`}>
                  <div className="w-10 h-10 rounded-full bg-[#F97316]/10 flex items-center justify-center text-sm font-semibold text-[#F97316]">
                    {u.prenom?.[0]}{u.nom?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{u.prenom} {u.nom}</p>
                    <p className="text-xs text-[#71717A]">{u.email}</p>
                  </div>
                  <Badge className={ROLE_COLORS[u.role] || 'bg-[#F3F4F6] text-[#6B7280]'} variant="secondary">
                    {ROLE_LABELS[u.role] || u.role}
                  </Badge>
                  <span className="text-xs text-[#71717A]">{u.date_creation ? new Date(u.date_creation).toLocaleDateString('fr-FR') : ''}</span>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => approveUser(u.id)} data-testid={`approve-user-${u.id}`}
                      className="h-8 gap-1 bg-[#10B981] hover:bg-[#059669] text-white text-xs">
                      <CheckCircle className="w-3.5 h-3.5" /> Valider
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => rejectUser(u.id)} data-testid={`reject-user-${u.id}`}
                      className="h-8 gap-1 text-[#EF4444] border-[#EF4444]/30 hover:bg-[#EF4444]/5 text-xs">
                      <XCircle className="w-3.5 h-3.5" /> Rejeter
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Users */}
      <Card className="border border-[#E5E7EB] shadow-sm">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                {['Nom', 'Email', 'Fonction', 'Statut', 'Créé le', 'Actions'].map(h => (
                  <th key={h} className="text-left py-3 px-4 text-[#71717A] font-medium text-xs uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-[#E5E7EB]/50 hover:bg-[#F9FAFB]/50" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-[#F3F4F6] flex items-center justify-center text-xs font-semibold text-[#71717A]">
                        {u.prenom?.[0]}{u.nom?.[0]}
                      </div>
                      <span className="font-medium">{u.prenom} {u.nom}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-[#71717A]">{u.email}</td>
                  <td className="py-3 px-4">
                    <Badge className={`text-xs ${ROLE_COLORS[u.role] || ROLE_COLORS.assistante}`} variant="secondary">
                      {ROLE_LABELS[u.role] || u.role}
                    </Badge>
                  </td>
                  <td className="py-3 px-4">
                    <Badge variant="secondary" className={`text-xs ${u.actif ? 'bg-[#ECFDF5] text-[#059669]' : 'bg-[#FEF2F2] text-[#DC2626]'}`}>
                      {u.actif ? 'Actif' : 'Bloqué'}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-[#71717A] text-xs">{u.date_creation ? new Date(u.date_creation).toLocaleDateString('fr-FR') : '-'}</td>
                  <td className="py-3 px-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`user-actions-${u.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem onClick={() => setEditUser({ id: u.id, prenom: u.prenom, nom: u.nom, email: u.email, role: u.role })}
                          data-testid={`edit-user-${u.id}`}>
                          <Pencil className="w-3.5 h-3.5 mr-2" /> Modifier le profil
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openPerms(u)} data-testid={`open-perms-${u.id}`}>
                          <Lock className="w-3.5 h-3.5 mr-2" /> Permissions
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setResetPw({ userId: u.id, password: '' })}>
                          <Key className="w-3.5 h-3.5 mr-2" /> Réinitialiser mot de passe
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => toggleActive(u.id, u.actif)} data-testid={`toggle-active-${u.id}`}
                          className={u.actif ? 'text-[#D97706]' : 'text-[#059669]'}>
                          {u.actif ? <><Ban className="w-3.5 h-3.5 mr-2" /> Bloquer l'accès</> : <><Power className="w-3.5 h-3.5 mr-2" /> Réactiver</>}
                        </DropdownMenuItem>
                        {isSuperAdmin && u.id !== currentUser?.id && (
                          <DropdownMenuItem onClick={() => hardDeleteUser(u)} data-testid={`hard-delete-${u.id}`}
                            className="text-[#DC2626] focus:bg-[#FEF2F2] focus:text-[#991B1B]">
                            <Trash2 className="w-3.5 h-3.5 mr-2" /> Supprimer définitivement
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPw.userId} onOpenChange={() => setResetPw({ userId: null, password: '' })}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>Réinitialiser le mot de passe</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-sm mb-1 block">Nouveau mot de passe</Label>
              <Input type="password" value={resetPw.password} onChange={e => setResetPw(r => ({ ...r, password: e.target.value }))}
                className="h-9 bg-white" data-testid="new-password-input" />
            </div>
            <Button onClick={resetPassword} data-testid="confirm-reset-password"
              className="w-full h-10 bg-[#F97316] hover:bg-[#EA580C] text-white">Réinitialiser</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={!!permsUser} onOpenChange={(o) => !o && setPermsUser(null)}>
        <DialogContent className="max-w-2xl" data-testid="permissions-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
              Permissions — {permsUser?.prenom} {permsUser?.nom}
            </DialogTitle>
            <p className="text-xs text-[#71717A] mt-1">
              Rôle : <span className="font-medium">{ROLE_LABELS[permsUser?.role] || permsUser?.role}</span>
              {(permsUser?.role === 'super_admin' || permsUser?.role === 'admin') && (
                <span className="ml-2 text-[#F97316]">Accès total — non personnalisable.</span>
              )}
            </p>
          </DialogHeader>
          {(permsUser?.role === 'super_admin' || permsUser?.role === 'admin') ? (
            <div className="p-6 text-sm text-[#71717A] text-center">
              Les administrateurs ont toujours accès à toutes les fonctionnalités.
            </div>
          ) : permsSchema && permsOverride ? (
            <div className="space-y-4 pt-2 max-h-[60vh] overflow-y-auto">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-[#FFF7ED] border border-[#F97316]/30">
                <Checkbox
                  id="perms-use-override"
                  checked={permsUseOverride}
                  onCheckedChange={(v) => {
                    const on = !!v;
                    setPermsUseOverride(on);
                    if (!on) setPermsOverride(permsEffective || {});
                  }}
                  data-testid="perms-use-override"
                />
                <label htmlFor="perms-use-override" className="text-sm cursor-pointer flex-1">
                  <strong>Personnaliser les permissions</strong>
                  <span className="block text-xs text-[#71717A] mt-0.5">
                    Décochez pour revenir aux permissions par défaut du rôle.
                  </span>
                </label>
              </div>

              <table className="w-full text-sm border border-[#E5E7EB] rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-[#71717A]">Ressource</th>
                    <th className="py-2 px-3 text-xs uppercase tracking-wider text-[#71717A] text-center">Voir</th>
                    <th className="py-2 px-3 text-xs uppercase tracking-wider text-[#71717A] text-center">Créer</th>
                    <th className="py-2 px-3 text-xs uppercase tracking-wider text-[#71717A] text-center">Modifier</th>
                    <th className="py-2 px-3 text-xs uppercase tracking-wider text-[#71717A] text-center">Supprimer</th>
                    <th className="py-2 px-3 text-xs uppercase tracking-wider text-[#71717A] text-center">Déplacer</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(permsSchema.resources).map(([resource, actions]) => (
                    <tr key={resource} className="border-b border-[#E5E7EB]/70">
                      <td className="py-2 px-3 font-medium capitalize">{resource}</td>
                      {['view', 'create', 'edit', 'delete', 'move'].map(action => {
                        const available = actions.includes(action);
                        const checked = (permsOverride[resource] || []).includes(action);
                        return (
                          <td key={action} className="py-2 px-3 text-center">
                            {available ? (
                              <Checkbox
                                checked={checked}
                                disabled={!permsUseOverride}
                                onCheckedChange={() => togglePerm(resource, action)}
                                data-testid={`perm-${resource}-${action}`}
                              />
                            ) : (
                              <span className="text-[#CBD5E1]">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setPermsUser(null)} className="flex-1 h-10">
                  Annuler
                </Button>
                <Button onClick={savePerms} data-testid="save-perms-btn"
                  className="flex-1 h-10 bg-[#F97316] hover:bg-[#EA580C] text-white">
                  Enregistrer
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-6 text-sm text-center text-[#71717A]">Chargement...</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>Modifier le profil</DialogTitle>
          </DialogHeader>
          {editUser && (
            <form onSubmit={(e) => { e.preventDefault(); saveEditUser(); }} className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm mb-1 block">Prénom</Label>
                  <Input value={editUser.prenom} onChange={e => setEditUser(u => ({ ...u, prenom: e.target.value }))}
                    required className="h-9 bg-white" data-testid="edit-prenom" />
                </div>
                <div>
                  <Label className="text-sm mb-1 block">Nom</Label>
                  <Input value={editUser.nom} onChange={e => setEditUser(u => ({ ...u, nom: e.target.value }))}
                    required className="h-9 bg-white" data-testid="edit-nom" />
                </div>
              </div>
              <div>
                <Label className="text-sm mb-1 block">Email</Label>
                <Input type="email" value={editUser.email}
                  onChange={e => setEditUser(u => ({ ...u, email: e.target.value }))}
                  required className="h-9 bg-white" data-testid="edit-email" />
              </div>
              <div>
                <Label className="text-sm mb-1 block">Fonction</Label>
                <Select value={editUser.role} onValueChange={v => setEditUser(u => ({ ...u, role: v }))}>
                  <SelectTrigger className="h-9 bg-white" data-testid="edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#71717A] mt-1">Changer la fonction applique automatiquement les permissions par défaut du nouveau rôle (sauf si override personnel).</p>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditUser(null)} className="flex-1 h-10">Annuler</Button>
                <Button type="submit" data-testid="save-edit-user"
                  className="flex-1 h-10 bg-[#F97316] hover:bg-[#EA580C] text-white">
                  Enregistrer
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
